import React from 'react';
import BlockUi from 'react-block-ui';
import { WarningRounded } from '@material-ui/icons';
import { Button } from '../../../../../components/Button/Button';
import PaymentLinkModal from '../../../../../components/PaymentLinkModal';
import ModalConfirm from '../../../../../components/ModalConfirm';
import { formatCurrency } from '../../../../Produtos/utils/formatCurrency';
import { formatDate, formatDateLong } from '../../../../../helpers/formatDate';
import { useFinancialBootstrap } from './financial.bootstrap.hook';
import { InfoCard } from './components/InfoCard';
import { DetailRow } from './components/DetailRow';
import { ListRow } from './components/ListRow';
import { FinancialActionsMenu } from './components/FinancialActionsMenu';
import { BlockAccessModal } from './_modal/BlockAccessModal';
import { RemoveItemModal } from './_modal/RemoveItemModal';
import CancelModal from '../../CancelModal';
import PixModal from '../../PixModal';
import Block from '@mui/icons-material/Block';

const ACTIVE_STATUSES = ['active', 'paid', 'trialing'];

const PAYMENT_METHODS = {
  credit_card: 'Cartão de crédito',
  boleto: 'Boleto',
  pix: 'Pix',
};
const canceledStatus = ['canceled', 'CANCELED'];

export default function Financial(props) {
  const {
    isLoadingClientSubscription,
    isSettlingCharge,
    isLoadingPaymentLink,
    clientSubscription,
    isJussubClient,
    paymentLinkModal,
    setPaymentLinkModal,
    handleUpdatePlanOrActivateButton,
    isPixPaymentEnabled,
    handlePix,
    pixLoading,
    isPixModal,
    setIsPixModal,
    pixData,
    canCancelPlan,
    isCancelingModal,
    setIsCancelingModal,
    cancelClick,
    handleCancel,
    isClientAccessControlEnabled,
    isClientAccessBlocked,
    isBlockingClientAccess,
    isBlockAccessModalOpen,
    setIsBlockAccessModalOpen,
    isUnblockAccessModalOpen,
    setIsUnblockAccessModalOpen,
    blockAccessExpiresAt,
    setBlockAccessExpiresAt,
    isIndefiniteBlockAccess,
    setIsIndefiniteBlockAccess,
    handleToggleClientAccess,
    blockClientAccess,
    unblockClientAccess,
    removeItemModal,
    isRemovingItem,
    openRemoveItemModal,
    closeRemoveItemModal,
    confirmRemoveItem,
  } = useFinancialBootstrap({ props });

  const info = clientSubscription.info ?? {};

  return (
    <BlockUi
      tag="div"
      blocking={isLoadingClientSubscription || isSettlingCharge}
    >
      <div>
        <div className="d-flex justify-content-between align-items-start mb-18">
          <h1 className="azul-cabecalho m-0">Detalhes do plano</h1>
          <div className="d-flex" style={{ gap: 12 }}>
            {isClientAccessBlocked && (
              <div className="d-flex justify-content-between align-items-center text-danger">
                <Block fontSize="medium" className="mr-2" />
                <p style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>
                  Acesso bloqueado
                </p>
              </div>
            )}
            <Button outlined>Criar oferta</Button>
            <Button
              primary
              onClick={() => handleUpdatePlanOrActivateButton()}
              disabled={isLoadingPaymentLink}
            >
              {canceledStatus.includes(clientSubscription?.info?.status)
                ? 'REATIVAR PLANO'
                : 'UPGRADE DE PLANO'}
            </Button>
            <FinancialActionsMenu
              isPixPaymentEnabled={isPixPaymentEnabled}
              isPixLoading={pixLoading}
              onPayWithPix={handlePix}
              isClientAccessControlEnabled={isClientAccessControlEnabled}
              isClientAccessBlocked={isClientAccessBlocked}
              onToggleClientAccess={handleToggleClientAccess}
              canCancelPlan={canCancelPlan}
              onCancelPlan={cancelClick}
            />
          </div>
        </div>

        {Object.keys(clientSubscription).length > 0 && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
              }}
              className="mb-12"
            >
              <InfoCard
                title="Plano atual"
                text={clientSubscription.plan_details?.name ?? info.plan}
                active={ACTIVE_STATUSES.includes(info.status)}
              />
              <InfoCard
                title="Valor da próxima cobrança"
                text={formatCurrency(info.plan_amount)}
              />
              <InfoCard
                title="Próxima cobrança"
                text={formatDateLong(info.current_period_end)}
              />
            </div>

            <div className="mb-6 d-flex flex-column" style={{ gap: 12 }}>
              <DetailRow label="Valor do plano" boldValue>
                {formatCurrency(info.plan_amount)}
              </DetailRow>
              <DetailRow label="Recorrência">
                {`A cada ${clientSubscription.plan_details?.periodicity ??
                  '-'} dias`}
              </DetailRow>
              <DetailRow label="Início do período atual">
                {formatDate(info.current_period_start)}
              </DetailRow>
              <DetailRow label="Fim do período atual">
                {formatDate(info.current_period_end)}
              </DetailRow>
              <DetailRow label="Forma de pagamento">
                {PAYMENT_METHODS[info.payment_method] ??
                  info.payment_method ??
                  '-'}
                {info.tickets?.[0]?.pdf && (
                  <a
                    href={info.tickets[0].pdf}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2"
                  >
                    Acessar boleto
                  </a>
                )}
              </DetailRow>
            </div>

            <hr style={{ border: 0, borderTop: '1px solid #eaeaea' }} />

            <div className="mt-6 mb-6">
              <h3
                className="azul-cabecalho font-weight-boldest"
                style={{ fontSize: 14 }}
              >
                Add-ons
              </h3>
              {info.addons?.length > 0 ? (
                <div className="d-flex flex-column" style={{ gap: 12 }}>
                  {info.addons.map((addon, index) => (
                    <ListRow
                      key={addon.id ?? index}
                      labelPrimary={addon.name}
                      labelSecondary={`${addon.itemCount}x ${
                        addon.itemCount > 1 ? 'itens' : 'item'
                      }`}
                      value={`${formatCurrency(addon.unitPrice)}`}
                      showDivider={index < info.addons.length - 1}
                      firstActionLabel="Detalhes do produto"
                      onFirstAction={() => {}}
                      secondActionLabel="Remover adicional"
                      onSecondAction={() => openRemoveItemModal('addon', addon)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">Nenhum add-on cadastrado.</p>
              )}
            </div>

            <hr className="mb-6 mt-6"></hr>
            <div className="mb-10">
              <h3
                className="azul-cabecalho font-weight-boldest"
                style={{ fontSize: 14 }}
              >
                Cupom
              </h3>
              {info.coupons?.length > 0 ? (
                <div className="d-flex flex-column" style={{ gap: 12 }}>
                  {info.coupons.map((coupon, index) => (
                    <ListRow
                      key={coupon.id ?? index}
                      labelPrimary={coupon.name}
                      labelSecondary={`Vence ${formatDate(coupon.expiresAt)}`}
                      primaryText={true}
                      showDivider={index < info.coupons.length - 1}
                      firstActionLabel="Detalhes do cupom"
                      onFirstAction={() => {}}
                      secondActionLabel="Remover cupom"
                      onSecondAction={() =>
                        openRemoveItemModal('coupon', coupon)
                      }
                    />
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">Nenhum cupom aplicado.</p>
              )}
            </div>
            <hr className="mb-6"></hr>

            <DetailRow label="Total" boldRow>
              {formatCurrency(info.charged_amount ?? info.plan_amount)}
            </DetailRow>
          </>
        )}
      </div>

      {paymentLinkModal.isOpen && (
        <PaymentLinkModal
          isOpen={paymentLinkModal.isOpen}
          onClose={() =>
            setPaymentLinkModal({ ...paymentLinkModal, isOpen: false })
          }
          originFlow={paymentLinkModal.originFlow}
          paymentLink={paymentLinkModal.data}
          isJussub={isJussubClient}
        />
      )}

      {isPixModal &&
        (pixData?.charges?.[0]?.last_transaction ||
          pixData?.last_transaction) && (
          <PixModal
            setOpen={setIsPixModal}
            pixCode={
              pixData.charges?.[0]?.last_transaction?.qr_code ||
              pixData?.last_transaction?.qr_code
            }
            pixQR={
              pixData.charges?.[0]?.last_transaction?.qr_code_url ||
              pixData?.last_transaction?.qr_code_url
            }
            pixTimer={pixData.timer}
            clientId={clientSubscription.client_id}
          />
        )}

      {isCancelingModal && (
        <CancelModal setOpen={setIsCancelingModal} onConfirm={handleCancel} />
      )}

      {isBlockAccessModalOpen && (
        <BlockAccessModal
          isLoading={isBlockingClientAccess}
          blockAccessExpiresAt={blockAccessExpiresAt}
          setBlockAccessExpiresAt={setBlockAccessExpiresAt}
          isIndefiniteBlockAccess={isIndefiniteBlockAccess}
          setIsIndefiniteBlockAccess={setIsIndefiniteBlockAccess}
          onConfirm={blockClientAccess}
          onClose={() => {
            setIsBlockAccessModalOpen(false);
            setBlockAccessExpiresAt('');
            setIsIndefiniteBlockAccess(false);
          }}
        />
      )}

      {isUnblockAccessModalOpen && (
        <ModalConfirm
          icon={WarningRounded}
          title="Desbloquear acesso do cliente?"
          message="Esta ação permitirá que o cliente possa acessar a plataforma Jusfy até o final do período vigente."
          onConfirm={unblockClientAccess}
          onClose={() => setIsUnblockAccessModalOpen(false)}
          isLoading={isBlockingClientAccess}
        />
      )}

      {removeItemModal.isOpen && (
        <RemoveItemModal
          title={
            removeItemModal.type === 'addon'
              ? 'Deseja remover este produto?'
              : 'Deseja excluir o cupom?'
          }
          subtitle={
            removeItemModal.type === 'addon'
              ? 'Ao confirmar, o cliente não terá mais acesso ao recurso adicional na renovação da assinatura.'
              : 'Ao confirmar, este cupom não poderá ser mais utilizado e nem oferecido para nenhum cliente.'
          }
          isLoading={isRemovingItem}
          confirmButtonMessage={
            removeItemModal.type === 'addon'
              ? 'Remover produto'
              : 'Excluir o cupom?'
          }
          onConfirm={confirmRemoveItem}
          onClose={closeRemoveItemModal}
        />
      )}
    </BlockUi>
  );
}
